import { DateTime } from "luxon"

import RenderKPI from "@/components/RenderKPI.tsx"
import RenderLocation from "@/components/RenderLocation.tsx"
import RenderType from "@/components/RenderType.tsx"
import RenderUnit from "@/components/RenderUnit.tsx"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs.tsx"
import AdminTable from "@/features/admin/components/AdminTable.tsx"
import CacheRefresh from "@/features/admin/components/CacheRefresh"
import CSVImporter from "@/features/admin/components/CSVImporter.tsx"
import type { AdminTableConfig } from "@/features/admin/types/adminTableTypes.ts"
import { getAllKPIs } from "@/features/kpis/api/getAllKPIs.ts"
import { getAllKPIValues } from "@/features/kpis/api/getAllKPIValues.ts"
import KPIEditor from "@/features/kpis/components/KPIEditor.tsx"
import KPIValueEditor from "@/features/kpis/components/KPIValueEditor.tsx"
import type { IdentifiedKPIValue, KPIDefinition } from "@/types/kpiTypes.ts"

export default function KPIAdmin() {
  const defConfig: AdminTableConfig<KPIDefinition> = {
    name: "kpis",
    fetcher: getAllKPIs,
    editor: KPIEditor,
    pageSize: 30,
    columns: [
      {
        name: "id",
        heading: "ID",
        filter: false,
        type: "number",
        displayFunc: (v: number) => <>{v}</>,
      },
      {
        name: "name",
        heading: "Name",
        filter: false,
        type: "string",
        displayFunc: (v: string) => <>{v}</>,
      },
      {
        name: "unitId",
        heading: "Units",
        filter: false,
        type: "string",
        displayFunc: (v: number) => <RenderUnit id={v} />,
      },
    ],
  }

  const valConfig: AdminTableConfig<IdentifiedKPIValue> = {
    name: "kpiValues",
    fetcher: getAllKPIValues,
    pageSize: 30,
    editor: KPIValueEditor,
    columns: [
      {
        name: "kpiId",
        heading: "KPI",
        filter: true,
        type: "number",
        width: 500,
        displayFunc: (v: number) => <RenderKPI id={v} />,
      },
      {
        name: "date",
        heading: "Date",
        width: 200,
        filter: true,
        type: "date",
        displayFunc: (v: Date) => (
          <>{DateTime.fromJSDate(v).setZone("UTC").toLocaleString(DateTime.DATE_SHORT)}</>
        ),
      },
      {
        name: "locationId",
        heading: "Location",
        filter: true,
        width: 250,
        type: "number",
        displayFunc: (v: number) => <RenderLocation id={v} />,
      },
      {
        name: "typeId",
        heading: "Type",
        filter: true,
        width: 200,
        type: "number",
        displayFunc: (v: number) => <RenderType id={v} />,
      },
      {
        name: "value",
        heading: "Value",
        filter: false,
        type: "number",
        displayFunc: (v: number) => <>{v}</>,
      },
    ],
  }

  return (
    <>
      <div></div>
      <Tabs defaultValue="defs">
        <div>
          <TabsList>
            <TabsTrigger value="defs">KPI Definitions</TabsTrigger>
            <TabsTrigger value="values">KPI Values</TabsTrigger>
          </TabsList>
          <div className="float-right">
            <CacheRefresh />
          </div>
        </div>
        <TabsContent value="defs">
          <div className="rounded-md border shadow-sm">
            <AdminTable<KPIDefinition> config={defConfig} />
          </div>
        </TabsContent>
        <TabsContent value="values">
          <div className="rounded-md border shadow-sm p-4">
            <CSVImporter config={{ label: "Bulk KPI Import", endpoint: "kpis/bulk" }} />
          </div>
          <div className="rounded-md border shadow-sm mt-4">
            <AdminTable<IdentifiedKPIValue> config={valConfig} />
          </div>
        </TabsContent>
      </Tabs>
    </>
  )
}
